import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Pencil, Trash2, Package } from "lucide-react";
import { toast } from "sonner";

const formatMXN = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

const empty = {
  barcode: "", name: "", sku: "", category_id: "",
  price: 0, cost: 0, stock: 0, min_stock: 5, unit: "pza", image_url: "", description: "",
};

export default function Products() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = { q: q || undefined };
    if (filterCat && filterCat !== "all") params.category_id = filterCat;
    const { data } = await api.get("/products", { params });
    setItems(data);
  };

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [q, filterCat]); // eslint-disable-line

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      barcode: p.barcode, name: p.name, sku: p.sku || "",
      category_id: p.category_id || "", price: p.price, cost: p.cost || 0,
      stock: p.stock, min_stock: p.min_stock, unit: p.unit || "pza",
      image_url: p.image_url || "", description: p.description || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        category_id: form.category_id || null,
        price: Number(form.price) || 0,
        cost: Number(form.cost) || 0,
        stock: parseInt(form.stock) || 0,
        min_stock: parseInt(form.min_stock) || 0,
      };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Producto creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Producto eliminado");
      load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="products-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-clay font-semibold">Catálogo</div>
          <h1 className="font-outfit text-4xl font-bold text-stone">Productos</h1>
          <p className="text-stone/60 mt-1">Gestiona tus pisos, azulejos y materiales.</p>
        </div>
        <Button
          onClick={openNew}
          data-testid="product-new-button"
          className="bg-clay hover:bg-clay-hover text-white shadow-[0_4px_14px_rgba(200,90,64,0.39)] hover:-translate-y-0.5 transition-all"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo producto
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-warm-border overflow-hidden shadow-sm">
        <div className="p-4 flex items-center gap-3 border-b border-warm-border">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone/40" />
            <Input
              placeholder="Buscar por nombre, código o SKU..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="product-search"
              className="pl-10 border-warm-border focus-visible:ring-clay focus-visible:ring-offset-0"
            />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-56 border-warm-border" data-testid="product-category-filter">
              <SelectValue placeholder="Todas las categorías" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <table className="w-full" data-testid="products-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone/50">
              <th className="px-6 py-3 font-semibold">Producto</th>
              <th className="px-6 py-3 font-semibold">Categoría</th>
              <th className="px-6 py-3 font-semibold">Código</th>
              <th className="px-6 py-3 font-semibold text-right">Precio</th>
              <th className="px-6 py-3 font-semibold text-right">Stock</th>
              <th className="px-6 py-3 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-warm-border hover:bg-sand/60 transition-colors">
                <td className="px-6 py-3">
                  <div className="flex items-center gap-3">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="w-10 h-10 rounded-lg object-cover border border-warm-border" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-sand border border-warm-border flex items-center justify-center text-stone/40">
                        <Package className="w-4 h-4" />
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-stone">{p.name}</div>
                      {p.sku && <div className="text-xs text-stone/50 font-mono">{p.sku}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-3 text-sm text-stone/70">{catName(p.category_id)}</td>
                <td className="px-6 py-3 text-sm font-mono text-stone/70">{p.barcode}</td>
                <td className="px-6 py-3 text-right font-outfit font-semibold text-stone">
                  {formatMXN(p.price)}
                </td>
                <td className="px-6 py-3 text-right">
                  <span className={`inline-block px-2.5 py-0.5 rounded-md text-sm font-medium ${
                    p.stock <= p.min_stock ? "bg-amber/15 text-[#B97A30]" : "bg-sage/15 text-[#5C7363]"
                  }`}>
                    {p.stock} {p.unit}
                  </span>
                </td>
                <td className="px-6 py-3 text-right">
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`product-edit-${p.id}`}
                    className="p-2 rounded-md hover:bg-sand text-stone/60 hover:text-clay"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => del(p)}
                    data-testid={`product-delete-${p.id}`}
                    className="p-2 rounded-md hover:bg-clay/10 text-stone/60 hover:text-clay"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-stone/50">
                  Sin productos. Crea el primero.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl bg-bone border-warm-border" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-2xl">
              {editing ? "Editar producto" : "Nuevo producto"}
            </DialogTitle>
            <DialogDescription>Completa la información del producto.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Código de barras *">
              <Input
                value={form.barcode}
                onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                data-testid="product-barcode-input"
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </Field>
            <Field label="SKU">
              <Input
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </Field>
            <Field label="Nombre *" full>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="product-name-input"
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </Field>
            <Field label="Categoría">
              <Select
                value={form.category_id || "none"}
                onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="border-warm-border bg-white">
                  <SelectValue placeholder="Sin categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin categoría</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Unidad">
              <Input
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </Field>
            <Field label="Precio venta *">
              <Input
                type="number" step="0.01"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                data-testid="product-price-input"
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </Field>
            <Field label="Costo">
              <Input
                type="number" step="0.01"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </Field>
            <Field label="Stock actual">
              <Input
                type="number"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                data-testid="product-stock-input"
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </Field>
            <Field label="Stock mínimo">
              <Input
                type="number"
                value={form.min_stock}
                onChange={(e) => setForm({ ...form, min_stock: e.target.value })}
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </Field>
            <Field label="URL imagen" full>
              <Input
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://..."
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </Field>
            <Field label="Descripción" full>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </Field>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-warm-border">
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="product-save-button"
              className="bg-clay hover:bg-clay-hover text-white"
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-xs uppercase tracking-wider font-semibold text-stone/70 mb-1.5 block">
        {label}
      </label>
      {children}
    </div>
  );
}
